import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';
import { DtoBase, TenantModels } from '@project44-models/iam-gateway';

import { AvatarComponent } from '../../../../../shared/core/infrastructrue/ui/Avatar/Avatar.component';
import { TTypography } from '../../../../../shared/core/infrastructrue/ui/Typography/Typography.view';
import { TenantTileCardComponent } from './TenantTileCard.component';

export type TTenantTileCard = {
  tenantDto: DtoBase<TenantModels.TenantViewModel>;
  isCurrentTenant?: boolean;
  handleOnClick?: (payload: {
    tenantDto: DtoBase<TenantModels.TenantViewModel>;
  }) => void;
  isLoading?: boolean;
  highlightableConfig?: TTypography['highlightableConfig'];
  config: TenantTileCardComponent;
} & Partial<JSX.IntrinsicAttributes>;

export const TenantTileCardView: React.FC<TTenantTileCard> = ({
  tenantDto,
  config,
  handleOnClick,
  isCurrentTenant,
  isLoading,
  highlightableConfig,
}) => {
  const {
    colorBrandGradient,
    colorPaletteBlue100,
    colorPaletteBlue600,
    colorBackgroundSecondary,
    radiusSmall,
    fontSizeSmall,
    spaceSmall,
  } = ManifestUI.designTokens;

  return (
    <ManifestUI.Grid
      css={{
        minWidth: '300px',
        width: '100%',
        padding: spaceSmall,
        margin: `${spaceSmall} 0`,
        borderRadius: radiusSmall,
        ...(!isCurrentTenant &&
          !isLoading && {
            '&:hover': {
              background: colorBackgroundSecondary,
              cursor: 'pointer',
            },
          }),
      }}
      onClick={() => {
        handleOnClick && handleOnClick({ tenantDto });
      }}
      role={'presentation'}
      aria-label={'tenant-tile-card-view'}
      columns="repeat(12, 1fr)"
    >
      <ManifestUI.GridItem column={2}>
        {AvatarComponent.create({
          withLoadingStatusOptions: { isLoading },
          avatarProps: {
            fallback: config.buildAvatarName(tenantDto.source.getName()),
            css: {
              backgroundColor: colorPaletteBlue100,
              color: colorPaletteBlue600,
            },
          },
        })}
      </ManifestUI.GridItem>
      <ManifestUI.GridItem
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
        column={isCurrentTenant ? 9 : 10}
      >
        {config.composeMainText(tenantDto, highlightableConfig, isLoading)}{' '}
        <div>
          {!isLoading && !isCurrentTenant && (
            <ManifestUI.Tag
              css={{ marginRight: ManifestUI.designTokens.spaceSmall }}
              children={config.composeTenantPurposeEnumText(
                tenantDto.source.getPurpose(),
              )}
            />
          )}
          {config.composeSubtext(
            tenantDto,
            highlightableConfig,
            isCurrentTenant,
            isLoading,
          )}{' '}
        </div>
      </ManifestUI.GridItem>
      {isCurrentTenant && (
        <ManifestUI.GridItem
          css={{
            margin: 'auto',
            textAlign: 'center',
          }}
          column={1}
        >
          <ManifestUI.Icon
            data-testid={'currentTenantIcon'}
            css={{
              background: colorBrandGradient,
              backgroundClip: 'text',
              fontSize: fontSizeSmall,
              textFillColor: 'transparent',
              '-webkit-background-clip': 'text',
              '-webkit-text-fill-color': 'transparent',
            }}
            icon={'check'}
          />
        </ManifestUI.GridItem>
      )}
    </ManifestUI.Grid>
  );
};
